import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  Image,
  PinInput,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useMediaQuery } from "@mantine/hooks";
import useStyles from "./style";
import {
  resend2fa,
  resend2faSkypoint,
  verify2faSkypoint,
} from "../../services";
import LinePatternSvg from "../../components/Svgs/LinePatternSvg";
import { obfuscateEmail, obfuscatePhone } from "../../utils";

const SkypointTwoFactorAuthPage = () => {
  const { classes } = useStyles();
  const isResponsive = useMediaQuery("(max-width: 992px)");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const user = location?.state?.user;
  // Issued by /auth/skypoint/login in place of a session token (OD-1482).
  const pendingToken = location?.state?.pendingToken;
  console.log("🚀 ~ TwoFactorAuthPage ~ user:", user);

  if (!user) {
    navigate("/skypoint-login");
    return null; // Stop rendering
  }

  const handleVerify = async () => {
    try {
      setError("");

      if (otp?.length < 6) {
        setError("Please enter the complete 6-digit code.");
        return;
      }

      setVerifying(true);

      const values = {
        userId: user?.id,
        code: Number(otp),
        pendingToken,
      };
      const res = await verify2faSkypoint(values, navigate);
    } catch (error) {
      setOtp("");
      setError("");
      setVerifying(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    try {
      await resend2faSkypoint({ userId: user?.id, pendingToken });
    } catch (err) {}
  };

  return (
    <Grid className={classes.container}>
      <Grid.Col span={12} md={6} className={classes.leftCol}>
        <Flex direction="column" align="center" className={classes.leftContent}>
          <Box className={classes.LogoBar} pr={14} pb={20} h={76} w="100%">
            <Image
              src="/assets/images/skypoint-logo2.png"
              height={44}
              width={144}
            />
          </Box>
          <Flex
            direction="column"
            justify="center"
            gap={22}
            className={classes.formAndHdngWrapper}
          >
            <Flex direction="column" gap={12}>
              <Text fz={32} fw={600} ff="Inter" color="#171717">
                Two-Factor Authentication
              </Text>
              <Text fz={15} fw={400} ff="Inter" color="#737373">
                We've sent a 6-digit verification code to{" "}
                <b>
                  {user?.twoFactorType === "sms"
                    ? obfuscatePhone(user?.phone)
                    : user?.twoFactorType === "email"
                    ? obfuscateEmail(user?.email)
                    : ""}
                </b>
                .
                <br />
                Please enter it below to continue.
              </Text>
            </Flex>
            <Center>
              <PinInput
                length={6}
                size="xl"
                type="number"
                oneTimeCode
                value={otp}
                onChange={setOtp}
                placeholder="0"
                classNames={{
                  input: classes.pinInput,
                }}
              />
            </Center>
            {error && (
              <Text color="red" ta="center" ff="Inter">
                {error}
              </Text>
            )}
            <Button
              id="login-button"
              className={classes.loginBtn}
              loading={verifying}
              onClick={handleVerify}
            >
              {verifying ? "Verifying..." : "Verify Code"}
            </Button>
            <Text ta="center" ff="Inter" fz={14} color="#344054">
              Note: After <b>5 incorrect attempts</b>, your account will be
              locked for <b>30 minutes</b>.
            </Text>
            <Flex
              justify="center"
              align="center"
              gap={4}
              className={classes.resendLinkText}
            >
              <Text className="text">Didn’t receive the code?</Text>
              <UnstyledButton className="resendLink" onClick={handleResend}>
                Resend
              </UnstyledButton>
            </Flex>
            <Flex
              justify="center"
              gap={4}
              align="center"
              className={classes.resendLinkText}
            >
              <Text className="text">Want to use a different method?</Text>
              <UnstyledButton
                className="resendLink chooseLink"
                onClick={() =>
                  navigate("/skypoint-select-2fa-method", { state: { user, pendingToken } })
                }
              >
                Choose another
              </UnstyledButton>
            </Flex>
          </Flex>
        </Flex>
      </Grid.Col>

      <Grid.Col span={12} md={6} className={classes.rightCol}>
        <Flex
          className={classes.rightContent}
          align="center"
          justify="flex-end"
        >
          <Box className={classes.linePattern1Parent}>
            <LinePatternSvg />
          </Box>
          <img
            src="/assets/images/skypoint-dashboard.png"
            alt=""
            className={classes.dashboardImg}
          />
          <Box className={classes.linePattern2Parent}>
            <LinePatternSvg />
          </Box>
        </Flex>
      </Grid.Col>
    </Grid>
  );
};

export default SkypointTwoFactorAuthPage;
